import { useEffect, useMemo, useState } from 'react';
import { format as dateFnsFormat } from 'date-fns';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import { SimpleTooltip } from '@/components/ui/Tooltip/Tooltip';
import { showToast } from '@/components/ui/Toast';
import { useRorosCompanyProfileUpdate } from '@/hooks/roros/overarching';
import type { RorosCompanyProfileDTO } from '@/types/roros/overarching';
import { Info } from 'lucide-react';
import { t } from '@/lib/i18n';
import { useOverarchingEditable } from '../OverarchingEditableContext';

// ─── Profile field definitions ───────────────────────────────────────────────

export interface ProfileFieldDef {
  name: keyof RorosCompanyProfileDTO & string;
  label: string;
  type: 'text' | 'textarea' | 'date';
  placeholder?: string;
  hint?: string;
  validationQuestions?: string[];
}

export function purposeVisionFields(): ProfileFieldDef[] {
  return [
    {
      name: 'purpose',
      label: t('Purpose'),
      type: 'textarea',
      placeholder: t('Why does your organization exist?'),
      hint: t('Why does your company exist beyond making money? What would be lost if your company disappeared?'),
      validationQuestions: [
        t('Is it focused on us?'),
        t('Emotional, not just rational?'),
        t('Gives orientation in daily work?'),
      ],
    },
    {
      name: 'vision',
      label: t('Vision'),
      type: 'textarea',
      placeholder: t('What future do you want to create?'),
      hint: t('Imagine everything goes perfectly for 5 years. Where has your company arrived? One vivid sentence.'),
      validationQuestions: [
        t('Is it internal (not external)?'),
        t('Is it a destination (not an activity)?'),
      ],
    },
    {
      name: 'targetDate',
      label: t('Target date'),
      type: 'date',
      hint: t('When do you want to achieve this vision?'),
    },
    {
      name: 'mission',
      label: t('Mission'),
      type: 'textarea',
      placeholder: t('How do you achieve your vision?'),
      hint: t('How do you work toward your vision? What do you do for your customers?'),
    },
  ];
}

export function strategyFields(): ProfileFieldDef[] {
  return [
    {
      name: 'oneTerm',
      label: t('ONE Term'),
      type: 'text',
      placeholder: t('Your company in one word'),
      hint: t('Reduce everything your company does to ONE word or short phrase.'),
    },
    {
      name: 'strategySentence',
      label: t('Strategy Sentence'),
      type: 'textarea',
      placeholder: t('Your strategy in one sentence'),
      hint: t('Describe your strategy in one sentence. Does it make clear what you do NOT do?'),
    },
    {
      name: 'positioning',
      label: t('Positioning'),
      type: 'textarea',
      placeholder: t('How you position in the market'),
      hint: t('How do you position your company? What space do you own that others don\'t?'),
    },
    {
      name: 'elevatorPitch',
      label: t('Elevator Pitch'),
      type: 'textarea',
      placeholder: t('Your 30-second pitch'),
      hint: t('Someone asks: what does your company do? Answer in under 100 words.'),
    },
  ];
}

// ─── Profile field section (inline editable) ─────────────────────────────────

export function ProfileFieldsSection({
  title,
  profile,
  fields,
}: {
  title: string;
  profile: RorosCompanyProfileDTO;
  fields: ProfileFieldDef[];
}) {
  const updateMutation = useRorosCompanyProfileUpdate();
  const { canEdit } = useOverarchingEditable();
  const [form, setForm] = useState<Record<string, string>>({});
  const [initial, setInitial] = useState<Record<string, string>>({});

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const f of fields) {
      const v = profile[f.name];
      next[f.name] = v == null ? '' : String(v);
    }
    setForm(next);
    setInitial(next);
  }, [profile, fields]);

  const dirty = useMemo(
    () => fields.some((f) => (initial[f.name] ?? '') !== (form[f.name] ?? '')),
    [form, initial, fields],
  );

  const handleChange = (name: string, value: string) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  async function save() {
    const payload: Record<string, unknown> = {};
    for (const f of fields) {
      const v = form[f.name];
      payload[f.name] = v !== '' ? v : null;
    }
    try {
      await updateMutation.mutateAsync(payload);
      setInitial(form);
      showToast.success(t('Saved'));
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to save'));
    }
  }

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {canEdit && (
          <Button
            type="button"
            size="sm"
            onClick={() => void save()}
            isLoading={updateMutation.isPending}
            disabled={!dirty || updateMutation.isPending}
          >
            {t('Save')}
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {fields.map((f) => (
          <ProfileField
            key={f.name}
            field={f}
            value={form[f.name] ?? ''}
            onChange={(v) => handleChange(f.name, v)}
            readOnly={!canEdit}
          />
        ))}
      </div>
    </Card>
  );
}

function ProfileField({
  field,
  value,
  onChange,
  readOnly = false,
}: {
  field: ProfileFieldDef;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  const labelRow = (
    <div className="flex items-center gap-1.5">
      <span>{field.label}</span>
      {field.validationQuestions && field.validationQuestions.length > 0 && (
        <SimpleTooltip
          content={
            <ul className="list-disc list-inside space-y-0.5">
              {field.validationQuestions.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          }
          side="right"
        >
          <span className="inline-flex cursor-help">
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        </SimpleTooltip>
      )}
    </div>
  );

  if (field.type === 'textarea') {
    return (
      <Textarea
        label={labelRow as unknown as string}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        hint={field.hint}
        rows={6}
        readOnly={readOnly}
      />
    );
  }
  if (field.type === 'date') {
    const dateValue: Date | null = value
      ? (() => {
          const [y, m, d] = value.split('-').map(Number);
          return y && m && d ? new Date(y, m - 1, d) : null;
        })()
      : null;
    return (
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <DatePicker
            label={labelRow as unknown as string}
            value={dateValue}
            onChange={(date) => onChange(date ? dateFnsFormat(date, 'yyyy-MM-dd') : '')}
            hint={field.hint}
            disabled={readOnly}
          />
        </div>
        {value && !readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
            className="mb-[1px] shrink-0 text-sm text-muted-foreground hover:text-destructive h-auto p-0"
            title={t('Clear date')}
            aria-label={t('Clear date')}
          >
            ×
          </Button>
        )}
      </div>
    );
  }
  return (
    <Input
      label={labelRow as unknown as string}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      hint={field.hint}
      readOnly={readOnly}
    />
  );
}

// ─── Single-field helpers (industry, value prop, brand promise, etc.) ────────

export function SingleFieldSection({
  title,
  profile,
  field,
}: {
  title: string;
  profile: RorosCompanyProfileDTO;
  field: ProfileFieldDef;
}) {
  return <ProfileFieldsSection title={title} profile={profile} fields={[field]} />;
}
